import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, Package, Search, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

function ImageModal({ isOpen, onClose, imageUrl, productName }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] md:max-w-[70vw] lg:max-w-[50vw] p-0 overflow-hidden bg-black/90 border-white/10">
        <DialogTitle className="sr-only">
          Enlarged view of {productName}
        </DialogTitle>
        <div className="relative w-full h-full flex items-center justify-center min-h-[50vh]">
          <img
            src={imageUrl}
            alt={productName}
            className="max-w-full max-h-[85vh] object-contain"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-center font-medium">{productName}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    productName: "",
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') {
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedCategory);
        if (isUuid) {
          params.append('category_id', selectedCategory);
        } else {
          params.append('category_slug', selectedCategory);
        }
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      if (minPrice) {
        params.append('min_price', minPrice);
      }
      if (maxPrice) {
        params.append('max_price', maxPrice);
      }
      
      const response = await axios.get(`${API}/products`, { params });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setLoadError('We are waking up the catalog. Please wait a moment and try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery, minPrice, maxPrice]);

  useEffect(() => {
    fetchCategories();
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
    const searchParam = searchParams.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
    }
  }, [searchParams, fetchCategories]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
      window.scrollTo(0, 0);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchProducts]);

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="products-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="products-title">Our Collection</h1>
          <p className="text-base md:text-lg text-muted-foreground">Browse our curated selection of 3D printed masterpieces</p>
        </motion.div>

        {!loading && products.length > 0 && selectedCategory === 'all' && !searchQuery && (
          <div className="mb-16" data-testid="latest-products-section">
            <div className="flex items-center gap-2 mb-6">
              <div className="h-px flex-1 bg-white/10" />
              <h2 className="text-xl font-medium text-primary uppercase tracking-widest px-4">Latest Arrivals</h2>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {products.slice(0, 3).map((product, index) => (
                <motion.div
                  key={`latest-${product.id}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onClick={() => navigate(`/products/${product.slug}`)}
                  className="relative group cursor-pointer"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-accent rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-500" />
                  <div className="relative glass-morphic rounded-2xl overflow-hidden h-40 flex">
                    <div className="w-1/3 h-full overflow-hidden bg-muted/20">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={getImageUrl(product.images[0])}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 cursor-zoom-in"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageModal({
                              isOpen: true,
                              imageUrl: getImageUrl(product.images[0]),
                              productName: product.name,
                            });
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="w-2/3 p-4 flex flex-col justify-between">
                      <div>
                        <h3 className="font-medium text-foreground line-clamp-1">{product.name}</h3>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{product.description}</p>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-primary font-semibold">₹{product.price.toFixed(2)}</span>
                        <div className="flex items-center gap-1 text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full">
                          <Star className="h-3 w-3 fill-yellow-500" />
                          {product.average_rating?.toFixed(1) || '0.0'}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-12" data-testid="filter-section">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-64 bg-white/5 border-white/10" data-testid="category-filter">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.slug || cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search products or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 w-full"
              data-testid="search-input"
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Price:</span>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="Min"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="w-20 md:w-24 bg-white/5 border-white/10"
                data-testid="min-price-filter"
              />
              <span className="text-muted-foreground">-</span>
              <Input
                type="number"
                placeholder="Max"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="w-20 md:w-24 bg-white/5 border-white/10"
                data-testid="max-price-filter"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-8" data-testid="products-loading">
            <div className="flex items-center justify-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 px-5 py-4 text-center">
              <div className="h-3 w-3 animate-pulse rounded-full bg-primary" />
              <p className="text-sm md:text-base text-muted-foreground">
                Please wait while the page loads up.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-white/5"
                >
                  <div className="aspect-square animate-pulse bg-white/10" />
                  <div className="space-y-3 p-6">
                    <div className="h-6 w-3/4 animate-pulse rounded bg-white/10" />
                    <div className="h-4 w-full animate-pulse rounded bg-white/10" />
                    <div className="h-4 w-5/6 animate-pulse rounded bg-white/10" />
                    <div className="flex items-center justify-between pt-2">
                      <div className="h-7 w-24 animate-pulse rounded bg-white/10" />
                      <div className="h-10 w-28 animate-pulse rounded-full bg-white/10" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => navigate(`/products/${product.slug}`)}
                className="cursor-pointer"
                data-testid={`product-card-${product.id}`}
              >
                <div className="group glass-morphic rounded-2xl overflow-hidden hover:border-primary/30 transition-all duration-500">
                  <div className="aspect-square overflow-hidden bg-muted/20">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={getImageUrl(product.images[0])}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-16 w-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="p-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-medium text-foreground group-hover:text-primary transition-colors duration-300">
                        {product.name}
                      </h3>
                      <div className="flex items-center gap-1 bg-yellow-500/10 text-yellow-500 px-2 py-0.5 rounded-full text-xs font-semibold border border-yellow-500/10">
                        <Star className="h-3 w-3 fill-yellow-500" />
                        {product.average_rating?.toFixed(1) || '0.0'}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
                    {product.tags && product.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 py-1">
                        {product.tags.map(tag => (
                          <span key={tag} className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-2xl font-semibold text-primary">₹{product.price.toFixed(2)}</span>
                      <Button 
                        className="rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all duration-300"
                        data-testid={`view-product-${product.id}`}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-products">
            <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-xl text-muted-foreground">
              {loadError || 'No products available'}
            </p>
          </div>
        )}
      </div>

      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ ...imageModal, isOpen: false })}
        imageUrl={imageModal.imageUrl}
        productName={imageModal.productName}
      />
    </div>
  );
}
